import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import RenderPaymentMethod from "./RenderPaymentMethod";
import PlusIcon from "../../../../../images/common/add-inside-circle-blue.svg";
import styled from "styled-components";
import {
  ContactReferenceModel,
  MerchantAccount,
  PaymentMethodModel,
  PurchasePaymentIntentModel,
} from "../models";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  Input,
  SingleSelect,
  ToastService,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { PaymentMethodPriority } from "../enums";
import { AuthService, CheckOutService } from "../services";
import PaymentForm from "./PaymentForm";
import { PaymentMethodUtil } from "../utils";

interface PaymentMethodSelectorProps {
  selectedPaymentMethod?: PaymentMethodModel;
  paymentMethods: PaymentMethodModel[];
  contactId: string;
  onChange: (option: PaymentMethodModel) => void;
  hasError: boolean;
  hasErrorInNewPaymentMethod: boolean;
  className?: string;
  headerClass?: string;
  contact?: ContactReferenceModel | null;
  onNewPaymentMethod?: (isNewPaymentMethod: boolean, token: string) => void;
  merchantAccount?: MerchantAccount;
  isNewPaymentMethodPrimary: boolean;
  updateNewMethodAsPrimary: (value: boolean) => void;
  isLodingInProcess?: boolean;
  isRenderWithoutContact?: boolean;
}

const PaymentMethodWrapper = styled(FlexContainer)`
  button {
    border-radius: 4px;
    min-width: 85px;
    /* box-shadow: 1px 1px 2px 0px; */
    line-height: 13px;
  }
  .options-menu {
    padding: 10px 0px;
    max-height: 149px;
    > div:not(.new-payment-option) {
      /* padding: 0px 15px; */
      max-height: 92px;
    }
  }
  .new-payment-option {
    color: #556bb8;
    background-color: #f8f8f8;
    padding-left: 16px;
    border-radius: 0px 0px 8px 8px;
    img {
      height: 20px;
      width: 20px;
      margin-right: 10px;
    }
  }
  .options-menu,
  .select-header-span {
    button {
      min-width: 70px;
      padding: 5px 10px;
      height: 26px;
      font-size: 14px;
    }
  }
`;

const PaymentMethodDropdown: React.FC<PaymentMethodSelectorProps> = ({
  selectedPaymentMethod,
  paymentMethods,
  onChange,
  hasError,
  className,
  hasErrorInNewPaymentMethod,
  contact,
  onNewPaymentMethod,
  merchantAccount,
  contactId,
  headerClass,
  updateNewMethodAsPrimary,
  isNewPaymentMethodPrimary,
  isLodingInProcess,
  isRenderWithoutContact = false,
}) => {
  const [paymentIntent, setPaymentIntent] =
    useState<PurchasePaymentIntentModel | null>(null);
  const [isAPIRequestPending, setIsAPIRequestPending] = useState(false);
  const [createNewPaymentMethod, setCreateNewPaymentMethod] = useState(false);

  const contactPaymentMethods = paymentMethods.filter(
    (pm) => pm.contactId && pm.contactId === contactId
  );

  useEffect(() => {
    if (
      (isRenderWithoutContact || contactId) &&
      contactPaymentMethods.length === 0 &&
      !paymentIntent &&
      !isLodingInProcess &&
      merchantAccount?.id
    ) {
      if (isAPIRequestPending) {
        return;
      }
      createPaymentIntent();
      onNewPaymentMethod && onNewPaymentMethod(true, "");
    } else if (
      !createNewPaymentMethod &&
      !isLodingInProcess &&
      contactPaymentMethods.length > 0
    ) {
      setPaymentIntent(null);
    }
  }, [isRenderWithoutContact, contactId, paymentMethods]);

  function createPaymentIntent() {
    setIsAPIRequestPending(true);
    CheckOutService.createPaymentIntent(
      "None",
      AuthService.authData.accountId,
      null,
      merchantAccount?.createAPIModel2()
    ).subscribe({
      next: (result) => {
        if (!result.result) {
          ToastService.showError("Error in creating payment intent");
        } else {
          setIsAPIRequestPending(false);
          const paymentIntent = PurchasePaymentIntentModel.deserializeAPIModel(
            result.result
          );
          setPaymentIntent(paymentIntent);
          // setData(
          //     (prevData) =>
          //         new SubscriptionModel({
          //             ...prevData,
          //             paymentIntent: PurchasePaymentIntentModel.deserializeAPIModel(result.result),
          //             isNewPaymentMethod: true,
          //         }),
          // );
        }
      },
      error: () => {
        ToastService.showError("Error in creating payment intent");
      },
    });
  }

  const shouldRenderDropdown =
    !paymentIntent && (contactId === "" || contactPaymentMethods.length > 0); 
    
  return (
    <PaymentMethodWrapper fxLayout="column" className={headerClass}>
      {shouldRenderDropdown && (
        <SingleSelect
          selectedKey={selectedPaymentMethod?.card?.cardHolderName || ""}
          nameKeys={["name"]}
          valueKeys={["name"]}
          options={[contactPaymentMethods]}
          customHeader={
            contactPaymentMethods.length === 0 || !selectedPaymentMethod?.id ? (
              <CustomLabel>Select Payment Method</CustomLabel>
            ) : (
              <FlexContainer
                padding="0px 0px 5px"
                justifyContent="space-between"
                key={selectedPaymentMethod.id}
                alignItems="center"
              >
                {formatPaymentMethod(selectedPaymentMethod)}
                {selectedPaymentMethod.priority && (
                  <CustomButton
                    bgColor={
                      PaymentMethodUtil.getColors(
                        selectedPaymentMethod.priority
                      ).bgColor
                    }
                    color={
                      PaymentMethodUtil.getColors(
                        selectedPaymentMethod.priority
                      ).color
                    }
                    className="status-btn"
                  >
                    {PaymentMethodPriority[selectedPaymentMethod.priority]}
                  </CustomButton>
                )}
              </FlexContainer>
            )
          }
          customOption={(option: PaymentMethodModel) => (
            <FlexContainer
              padding="0px 16px 5px"
              key={option.id}
              alignItems="center"
              justifyContent="space-between"
            >
              {formatPaymentMethod(option)}
              {option?.priority && (
                <CustomButton
                  bgColor={PaymentMethodUtil.getColors(option.priority).bgColor}
                  color={PaymentMethodUtil.getColors(option.priority).color}
                  className="status-btn"
                >
                  {option?.priority}
                </CustomButton>
              )}
            </FlexContainer>
          )}
          customFooter={
            merchantAccount && (
              <FlexContainer
                alignItems="center"
                style={{
                  cursor:
                    !contactId && !isRenderWithoutContact ? "not-allowed" : "",
                }}
                onClick={() => {
                  if (!contactId && !isRenderWithoutContact) return;
                  createPaymentIntent();
                  setCreateNewPaymentMethod(true);
                  onNewPaymentMethod && onNewPaymentMethod(true, "");
                }}
              >
                <img src={PlusIcon} alt="New Payment Method" />
                <span>New Payment Method</span>
              </FlexContainer>
            )
          }
          footerClass="new-payment-option"
          onChange={onChange}
          className={`dpdn-with-icons ${className}`}
          searchable={true}
          hasError={hasError}
        />
      )}

      {paymentIntent && (
        <FlexContainer fxLayout="column">
          <PaymentForm
            paymentIntent={paymentIntent}
            onPaymentSuccess={(token: string) => {
              onNewPaymentMethod && onNewPaymentMethod(true, token);
            }}
            onPaymentError={() => {}}
            contact={contact || null}
          />
          {hasErrorInNewPaymentMethod && (
            <CustomLabel>
              <span className="red-text semi-bold">
                Please enter valid card details
              </span>
            </CustomLabel>
          )}
          <FlexContainer alignItems="center" padding="0px 0px 20px">
            <Input
              type="checkbox"
              value={isNewPaymentMethodPrimary}
              onChange={(e) =>
                updateNewMethodAsPrimary &&
                updateNewMethodAsPrimary(e.target.checked)
              }
              label={""}
              disabled={
                isRenderWithoutContact && contactPaymentMethods.length === 0
              }
              className={`${isRenderWithoutContact && contactPaymentMethods.length === 0 ? "not-allowed" : "clickable"} ${className}`}
            />
            <CustomLabel>Use as primary method for future payments</CustomLabel>
          </FlexContainer>
          <CustomButton
            onClick={() => {
              setPaymentIntent(null);
              onNewPaymentMethod && onNewPaymentMethod(false, "");
            }}
            disabled={contactPaymentMethods.length === 0}
          >
            Back
          </CustomButton>
        </FlexContainer>
      )}
    </PaymentMethodWrapper>
  );

  function formatPaymentMethod(option: PaymentMethodModel) {
    return (
      <div>
        <FlexContainer spacing={10}>
          <RenderPaymentMethod paymentMethod={option} />
          {option.card && (
            <CustomLabel>
              Expires {UtilityMethods.getMonth(option.card.expiryMonth)}{" "}
              {option.card.expiryYear}
            </CustomLabel>
          )}
        </FlexContainer>
      </div>
    );
  }
};

export default observer(PaymentMethodDropdown);
